// Catalogue data for the Admin: URL search params, API calls and TanStack Query options.
// Every response is parsed with the shared @enp/domain/api schema (lib/api.ts).

import {
  CATALOGUE_SORTS,
  catalogueCategoryListResponseSchema,
  catalogueProductDetailSchema,
  catalogueProductListResponseSchema,
  type CatalogueProductListResponse,
} from '@enp/domain/api';
import { productRoleSchema } from '@enp/domain/catalogue';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';

/** Products per page of the list. */
export const PAGE_SIZE = 200;

/**
 * The list's state in the URL (shareable, and Back works). Anything invalid is dropped
 * rather than failing the page. Defaults are left out: live products, name A–Z.
 */
export const catalogueSearchSchema = z.object({
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
  role: productRoleSchema.optional().catch(undefined),
  category: z.uuid().optional().catch(undefined),
  /** `only` archived, or `include` both; absent = live only. */
  archived: z.enum(['only', 'include']).optional().catch(undefined),
  /** Products with expiry states (`tracked`) or without (`untracked`). */
  expiry: z.enum(['tracked', 'untracked']).optional().catch(undefined),
  /** Only products that need someone (a recipe to sign off, a link to review). */
  attention: z.literal(true).optional().catch(undefined),
  sort: z.enum(CATALOGUE_SORTS).optional().catch(undefined),
});
/** The catalogue list's search params. */
export type CatalogueSearch = z.infer<typeof catalogueSearchSchema>;

/** The filters that narrow the list (everything but the sort order). */
export const FILTER_KEYS = ['q', 'role', 'category', 'archived', 'expiry', 'attention'] as const;

/** True when the search or a filter narrows the list. */
export function isFiltered(search: CatalogueSearch): boolean {
  return FILTER_KEYS.some((k) => search[k] !== undefined);
}

/** Drops undefined keys, so equal searches compare (and cache) equal. */
export function cleanSearch(search: CatalogueSearch): CatalogueSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([, v]) => v !== undefined),
  ) as CatalogueSearch;
}

/** The API query string for a page of the list. */
export function listQueryString(search: CatalogueSearch, cursor: string | null): string {
  const params = new URLSearchParams();
  if (search.q) params.set('q', search.q);
  if (search.role) params.set('role', search.role);
  if (search.category) params.set('category_id', search.category);
  if (search.archived) params.set('archived', search.archived);
  if (search.expiry) params.set('state_tracked', search.expiry === 'tracked' ? 'true' : 'false');
  if (search.attention) params.set('attention', 'true');
  if (search.sort) params.set('sort', search.sort);
  params.set('limit', String(PAGE_SIZE));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const catalogueKeys = {
  all: ['catalogue'] as const,
  list: (search: CatalogueSearch) => ['catalogue', 'products', cleanSearch(search)] as const,
  product: (id: string) => ['catalogue', 'product', id] as const,
  categories: () => ['catalogue', 'categories'] as const,
};

/** The product list, one page at a time ("Load more"). */
export const productListQuery = (search: CatalogueSearch) =>
  infiniteQueryOptions({
    queryKey: catalogueKeys.list(search),
    queryFn: ({ pageParam, signal }): Promise<CatalogueProductListResponse> =>
      api(`/catalogue/products?${listQueryString(search, pageParam)}`, {
        signal,
        schema: catalogueProductListResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

/** One product with everything about it. */
export const productQuery = (id: string) =>
  queryOptions({
    queryKey: catalogueKeys.product(id),
    queryFn: ({ signal }) =>
      api(`/catalogue/products/${encodeURIComponent(id)}`, {
        signal,
        schema: catalogueProductDetailSchema,
      }),
    retry: false,
  });

/** Live categories, for the filter. */
export const categoriesQuery = () =>
  queryOptions({
    queryKey: catalogueKeys.categories(),
    queryFn: ({ signal }) =>
      api('/catalogue/categories', { signal, schema: catalogueCategoryListResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });
